import React, { useState, useMemo, useEffect } from "react";
import "./App.css";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Paper,
  Chip,
  Autocomplete,
  Drawer,
  List,
  ListItem,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  ListItemButton,
  useMediaQuery
} from "@mui/material";
import { Check, HelpOutline, SkipNext, SkipPrevious, ExpandMore } from "@mui/icons-material";
import SquishedBubbles from "./SquishedVisualization";
import logo from "./logo.svg";
import HelpScreen from "./HelpScreen";
import config from "./config";
import { processHighlights, darkenColor, getTextColor, getParentColor } from "./utils";
import MinimalTextField from "./MinimalTextField";
import HighlightEmbed from "./HighlightEmbed";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const darkTheme = createTheme ({
  palette: {
    mode: 'dark',
  },
});

const highlights = require(`${config.highlightsJson}`);
const tagsColors = require(`${config.tagsColorsJson}`);

// Safely load medleys with null check and error handling
let medleys = [];
try {
  if (config.medleysJson) {
    medleys = require(`${config.medleysJson}`);
  }
} catch (error) {
  console.warn('Failed to load medleys:', error);
  medleys = [];
}

const project = config.projectTitle;
const projectDescription = config.projectDescription;

// Check if medleys are available
const hasMedleys = medleys && Array.isArray(medleys) && medleys.length > 0;

function App() {
  const isMobile = useMediaQuery('(max-width:600px)');

  const [selectedNode, setSelectedNode] = useState(null);
  const [showTopics, setShowTopics] = useState(!hasMedleys);
  const [isMedleyPlaying, setIsMedleyPlaying] = useState(null);
  const [currentMedleyHighlight, setCurrentMedleyHighlight] = useState(null);
  const [selectedMedley, setSelectedMedley] = useState(null)
  const [showNodeInfo, setShowNodeInfo] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [initiallySelectedHighlight, setInitiallySelectedHighlight] =
    useState(null);
  const [showHelp, setShowHelp] = useState(true);

  // Autocomplete value
  const [selectedHighlightOption, setSelectedHighlightOption] = useState(null);

  const processedHighlights = useMemo(
    () =>
      processHighlights(
        highlights,
        config.combineConversations,
        config.tagsField
      ),
    []
  );

  const {
    conversationIds,
    conversationToHighlights,
    //tagsToHighlights,
    parentTagsToHighlights,
  } = processedHighlights;

  const highlightOptions = useMemo(
    () =>
      highlights.map((highlight) => ({
        label: highlight.speaker ? `${highlight.speaker} (${highlight.id})` : `Anonymous (${highlight.id})` ,
        id: highlight.id,
      })),
    [highlights]
  );

  useEffect(() => {
    if (initiallySelectedHighlight) {
      const selectedHighlight = highlights.find(
        (highlight) => highlight.id === initiallySelectedHighlight
      );
      if (selectedHighlight) {
        handleNodeSelection(selectedHighlight);
      }
    }
  }, [initiallySelectedHighlight]);

  useEffect(() => {
    if (!isMobile) {
      setShowHelp(true);
    }
  }, [isMobile]);

  const handleNodeClick = (node) => {
    console.log('handleNodeClick called, node: ');
    console.log(node);
    handleNodeSelection(node);
    stopMedleyPlayback(node);
  };

  const handleNodeSelection = (node) => {
    setSelectedNode(node);
    setShowNodeInfo(true);

    if (node === null) {
      setShowNodeInfo(false);
      setSelectedTag(null);
      setSelectedHighlightOption(null);
      setIsPlaying(false);
    } else {
      // Find the matching option in highlightOptions
      const matchingOption = highlightOptions.find(
        (option) => option.id === node.id
      );
      setSelectedHighlightOption(matchingOption);

      // After the pan/zoom animation completes (1 second), start playing
      // On mobile, skip autoplay and require the user to press play manually
      if (!isMobile) {
        setTimeout(() => {
          setIsPlaying(true);
        }, 1000);
      }
    }
  };

  const handleHighlightSelect = (event, value) => {
    setSelectedHighlightOption(value);
    if (value) {
      setInitiallySelectedHighlight(value.id);
    } else {
      setInitiallySelectedHighlight(null);
    }
  };

  const [isPlaying, setIsPlaying] = useState(false);
  // Add state to track screensaver mode
  const [isScreensaverActive, setIsScreensaverActive] = useState(false);
  const handlePlayToggle = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleHelp = () => {
    setShowHelp(prev => !prev);
  };

  function getHighlightColor(id) {
    const highlight = highlights.find(
      (highlight) => highlight.id === id
    );
    const highlightColor = highlight.color;
    return highlightColor;
  }

  const handleMedleyClick = (medley) => {
    stopMedleyPlayback();
    if (medley === selectedMedley) {
      setSelectedMedley(null);
    } else {
      setSelectedMedley(medley);
      startMedleyPlayback(medley);
    }
  };

  const startMedleyPlayback = (medley) => {
    setIsMedleyPlaying(true);
    setInitiallySelectedHighlight(medley.highlights[0]);
    setCurrentMedleyHighlight(medley.highlights[0]);
  };

  function stopMedleyPlayback(node = null) {
    setIsMedleyPlaying(false);
    setSelectedMedley(null);
    setCurrentMedleyHighlight(null);
    setInitiallySelectedHighlight(node);
  };

  function nextMedleyHighlight(highlight) {
    let i = selectedMedley.highlights.indexOf(highlight);
    let nextHighlight = selectedMedley.highlights[i+1];
    if (nextHighlight === undefined) {
      stopMedleyPlayback();
    } else {
      console.log(nextHighlight);
      setCurrentMedleyHighlight(nextHighlight);
      setInitiallySelectedHighlight(nextHighlight);
    }
  }
  
  function prevMedleyHighlight(highlight) {
    let i = selectedMedley.highlights.indexOf(highlight);
    let prevHighlight = selectedMedley.highlights[i-1];
    if (prevHighlight === undefined) {
      stopMedleyPlayback();
    } else {
      console.log(prevHighlight);
      setCurrentMedleyHighlight(prevHighlight);
      setInitiallySelectedHighlight(prevHighlight);
    }
  }

  function chooseMedleyHighlight(i) {
    let highlight = selectedMedley.highlights[i];
    setCurrentMedleyHighlight(highlight);
    setInitiallySelectedHighlight(highlight);
  }

  useEffect(() => {
    return () => {
      setSelectedNode(null);
      stopMedleyPlayback();
    };
  }, []);

  const openTopics = () => {
    setSelectedMedley(null);
    stopMedleyPlayback();
    setShowTopics(prev => {
      return true;
    });
  };
  
  const closeTopics = () => {
    setShowTopics(prev => {
      return false;
    });
  };

  return (
    <ThemeProvider theme={darkTheme}>
    <div className="fullScreen" style={{ overflowY: 'none' }}>
      <AppBar 
        position="relative"
        sx={{ 
          backgroundColor: "rgb(20, 20, 20)", 
          zIndex: 1500,
          backgroundImage: 'none', 
          borderBottom: `.8px solid rgb(50, 50, 50)`,
          boxShadow: '2px 2px 10px rgb(0, 0, 0)'
        }}>
        <Toolbar sx={{
          justifyContent: isMobile ? "center" : "space-between",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          paddingTop: isMobile ? '.4rem' : undefined,
          paddingBottom: isMobile ? '.4rem' : undefined,
          minHeight: isMobile ? 'unset' : undefined,
        }}>
        {isMobile ? (
          <>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: .75, fontWeight: 500, lineHeight: 1.4 }}>
              <img src={logo} alt="SenseMap Logo" style={{ height: '14px', width: 'auto' }} />
              SenseMap
            </Typography>
            <Tooltip title={<Typography fontSize={20} padding={2}>{projectDescription}</Typography>}>
              <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.4, textAlign: 'center' }}>
                {project}
              </Typography>
            </Tooltip>
          </>
        ) : (
          <>
        <Typography variant="h6" sx={{ paddingTop: '1rem', paddingBottom: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={logo} alt="SenseMap Logo" style={{ height: '20px', width: 'auto' }} />
          SenseMap
        </Typography>
        <Tooltip title={<Typography fontSize={20} padding={2}>{projectDescription}</Typography>}>
          <Typography variant="h5" sx={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
            {project}
          </Typography>
        </Tooltip>
          </>
        )}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem'}}>
          <Autocomplete
            options={highlightOptions}
            getOptionLabel={(option) => option.label}
            onChange={handleHighlightSelect}
            value={selectedHighlightOption}
            renderInput={(params) => (
              <MinimalTextField
                {...params}
                variant="standard"
                placeholder="Search Participants"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: false,
                  sx: {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
            sx={{
              width: 180,
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          />
          <IconButton
            edge="end"
            color="inherit"
            aria-label="help"
            onClick={toggleHelp}
            sx={{ ml: 1 }}
          >
            <HelpOutline />
          </IconButton>
          </div>
        )}
        </Toolbar>
      </AppBar>
      {!isMobile && <Drawer
        anchor="left"
        variant="permanent"
        PaperProps={{
          sx: {
            backgroundColor: "rgb(20, 20, 20)",
            borderRight: `.8px solid rgb(50, 50, 50)`,
            elevation: 0,
            color: 'white',
            width: 350,
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
      <div style={{ marginBottom: '2rem' }}>
        <Box sx={{ 
          paddingTop: '84px',
          paddingRight: 2,
          paddingLeft: 2,
          flexShrink: 0,
        }}>
          <div style = {{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
            {hasMedleys && (
              <Button 
                variant="button" 
                onClick={closeTopics}
                sx = {{
                  p: '.4rem',
                  borderRadius: 2,
                  color: showTopics === true ? 'grey' : 'white',
                  cursor: 'pointer',
                  "&:hover": {
                    backgroundColor: showTopics === true ? 'rgb(45, 45, 45)' : 'none'
                  }
                }}
              >
                Medleys
              </Button>
            )}
            <Button variant="button"
              onClick={hasMedleys ? openTopics : undefined}
              sx = {{
                p: '.4rem',
                borderRadius: 2,
                color: hasMedleys ? (showTopics === false ? 'grey' : 'white') : 'white',
                cursor: hasMedleys ? 'pointer' : 'default',
                "&:hover": {
                  backgroundColor: hasMedleys ? (showTopics === false ? 'rgb(45, 45, 45)' : 'none') : 'none'
                }
              }}
            >
              Themes
            </Button>
          </div>
          { hasMedleys && !showTopics &&
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem',
            }}
          >
            Select a medley to follow a community narrative across conversations:
          </Typography> }
          { (showTopics || !hasMedleys) &&
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem',
            }}
          >
            Select a theme to see highlights related to that theme:
          </Typography> }
        </Box>
        { hasMedleys && !showTopics &&
        <List
          sx={{ marginBottom: 20 }}
        >
          {medleys.map((medley) => {
            let medleyName = medley.medley_name
            const selectedBackgroundColor = "rgb(38, 38, 38)";
            return (
            <ListItemButton
              key={medleyName}
              onClick={() => handleMedleyClick(medley)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'start',
                cursor: medley === selectedMedley ? 'auto' : 'pointer',
                backgroundColor: medley === selectedMedley ? selectedBackgroundColor : 'none',
                borderTop: medley === selectedMedley ? '.8px solid rgb(95, 95, 95)' : '.8px solid rgba(0, 0, 0, 0)',
                borderBottom: medley === selectedMedley ? '.8px solid rgb(95, 95, 95)' : '.8px solid rgba(0, 0, 0, 0)',
                "&:hover": {
                  backgroundColor: selectedBackgroundColor
                }
              }}
            >
              <Typography variant="body1" sx={{ alignSelf: 'start', fontWeight: 'medium' }}> {medleyName} </Typography>
              { medley === selectedMedley && 
              <div>
                <Typography variant="body1" sx={{ marginTop: 2, marginBottom: 2, paddingRight: 1, paddingLeft: 1 }}> {selectedMedley.medley_description} </Typography>
                <div style={{
                  display: 'flex',
                  justifyContent: 'start',
                  width: '100%',
                  gap: '.5rem',
                  alignItems: 'center'
                }}>
                  <div>
                    <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      prevMedleyHighlight(currentMedleyHighlight);
                    }}
                    sx={{ color: 'white' }}
                    >
                      <SkipPrevious />
                    </IconButton>
                  </div>
                  <div style={{ 
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center',
                  }}>
                  { selectedMedley.highlights.map((highlight) => {
                    let i = selectedMedley.highlights.indexOf(highlight);
                    let color = getHighlightColor(highlight);
                    return (
                      <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        chooseMedleyHighlight(i);
                      }}
                      style={{ 
                        cursor: 'pointer',
                        height: '20px',
                        width: '20px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        boxShadow: highlight === currentMedleyHighlight ? '0px 0px 20px white' : 'none',
                        border: '3px solid',
                        borderColor: highlight === currentMedleyHighlight ? 'white' : color
                      }}>
                      </div>
                    )
                  })}
                  </div>
                  <div>
                    <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      nextMedleyHighlight(currentMedleyHighlight);
                    }}
                    sx={{ color: 'white' }}
                    >
                      <SkipNext />
                    </IconButton>
                  </div>
                </div>
              </div>
              }
            </ListItemButton>
          )})}
        </List>
        }
        { (showTopics || !hasMedleys) &&
                <List>
                  {Object.keys(parentTagsToHighlights).map((tag) => {
                    let label = tag
                    let backgroundColor = getParentColor(tagsColors, label);
                    let textColor = getTextColor(backgroundColor);
                    return(
                      <ListItem key={label}>
                        <Accordion
                          sx={{
                            width: '100%',
                          }}
                        >
                          <AccordionSummary 
                            expandIcon={<ExpandMore />}
                            id={label}
                            sx={{
                              backgroundColor: backgroundColor,
                              color: textColor,
                              '&:hover': {
                                backgroundColor: darkenColor(backgroundColor),
                              }
                            }}
                          >
                            <Typography>{label}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {Object.keys(tagsColors).filter((tag) => tag.split(" :: ").includes(label)).map((tag) => {
                              let backgroundColor = tagsColors[tag];
                              let textColor = getTextColor(backgroundColor);
                              let chipLabel = tag.split(" :: ")[1];
                              if (chipLabel) {
                              return (
                                <Tooltip key={chipLabel} title={<Typography fontSize={14} padding={.5}>{chipLabel}</Typography>} placement='right'>
                                  <Chip 
                                    label={chipLabel}
                                    onClick={() => setSelectedTag(tag)}
                                    sx={{
                                      backgroundColor: backgroundColor,
                                      color: textColor,
                                      border: tag === selectedTag ? '1px solid white' : 'none',
                                      '&:hover': {
                                        backgroundColor: darkenColor(backgroundColor),
                                      },
                                      marginY: '.5rem',
                                      width: '100%',
                                      justifyContent: 'flex-start',
                                      fontSize: '0.9rem',
                                      height: '32px',
                                    }}
                                  />
                                </Tooltip>
                            )
                          }})}
                          </AccordionDetails>
                        </Accordion>
                    </ListItem>
                )})}
                </List>
        }
        </div>
      </Drawer>}
      <div className="visualizationContainer">
        <SquishedBubbles
          conversationIds={conversationIds}
          conversationToHighlights={conversationToHighlights}
          parentTagsToHighlights={parentTagsToHighlights}
          onNodeClick={handleNodeClick}
          selectedNode={selectedNode}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          initiallySelectedHighlight={initiallySelectedHighlight}
          onBackgroundClick={stopMedleyPlayback}
          isMedleyPlaying={isMedleyPlaying}
          // Pass a callback to update screensaver state
          onScreensaverActiveChange={setIsScreensaverActive}
        />
        {showNodeInfo && (
        <Paper style={{ 
          position: 'absolute',
          top: isMobile ? 0 : 84,
          right: isMobile ? 'auto' : 40,
          left: isMobile ? '50%' : 'auto',
          transform: isMobile ? 'translateX(-50%)' : 'none',
          width: isMobile ? '100%' : 400,
          maxHeight: isMobile ? '45vh' : 'none',
          overflowY: isMobile ? 'auto' : 'visible',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          padding: isMobile ? '1rem' : '1.5rem',
          marginBottom: isMobile ? 0 : 20,
          border: '.5px solid rgb(95, 95, 95)',
          borderRadius: isMobile ? '0 0 16px 16px' : 16
         }}>
          {selectedNode && (
            <div style = {{ }}>
              <HighlightEmbed
                highlight={selectedNode}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                muted={isScreensaverActive}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: "wrap",
                  paddingRight: '8px',
                  justifyContent: "center",
                  marginTop: 2,
                  scrollbarWidth: 'none',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
              >
                {selectedNode.tags.sort().map((tag) => {
                  const backgroundColor = tagsColors[tag] || "rgba(255, 255, 255, 0.5)";
                  const textColor = tagsColors[tag] ? getTextColor(backgroundColor) : "#000000";
                  const selectedBackgroundColor = tagsColors[tag] 
                    ? backgroundColor 
                    : "rgba(255, 255, 255, 0.8)";
                  const hoverBackgroundColor = tagsColors[tag] 
                    ? darkenColor(backgroundColor, 0.2) 
                    : "rgba(255, 255, 255, 0.9)";
                  return(           
                    <Chip
                      key={tag}
                      //size="small"
                      icon={tag === selectedTag ? <Check /> : null}
                      label={tag}
                      onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                      sx={{
                        backgroundColor: tag === selectedTag ? selectedBackgroundColor : backgroundColor,
                        color: tag === selectedTag && tagsColors[tag] ? textColor : (tagsColors[tag] ? textColor : "#000000"),
                        margin: 0.5,
                        "&:hover": {
                          backgroundColor: hoverBackgroundColor,
                        },
                        opacity: selectedTag && tag !== selectedTag ? 0.5 : 1,
                      }}
                    />
                  )})}
              </Box>
            </div>
          )}
        </Paper>
      )}
        {!isMobile && <HelpScreen open={showHelp} onClose={() => setShowHelp(false)} description={projectDescription} hasMedleys={hasMedleys}/>}
      </div>
    </div>
    </ThemeProvider>
  );
}

export default App;